/**
 * Debug configuration for SiteBuilderWizard
 *
 * This module encapsulates all debug-related logic for the wizard,
 * keeping the main component clean and focused on business logic.
 *
 * Usage:
 *   1. Set DEBUG_CONFIG.enabled = true
 *   2. Configure step, source, template/site as needed
 *   3. The wizard will auto-select and jump to the specified step
 */

// ============================================================================
// CONFIGURATION - Edit these values to control debug behavior
// ============================================================================

const DEBUG_CONFIG = {
	enabled: false, // Master switch for debug mode
	step: "TEMPLATE_SELECTION", // WIZARD_STEPS key to jump to (e.g., "TEMPLATE_SELECTION", "BRANDING", "USER_TYPES")
	source: "existing", // "premade" | "existing" | "scratch"
	template: "template-aged-care", // Template key (when source === "premade")
	site: "FakeCompany", // Site Key (when source === "existing")
	siteName: "SiteBuildTest1", // Pre-filled site name
};

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Check if debug mode is enabled
 */
export const isDebugEnabled = () => DEBUG_CONFIG.enabled;

/**
 * Get the full debug configuration
 */
export const getDebugConfig = () => ({ ...DEBUG_CONFIG });

/**
 * Get the debug step key (for logging)
 */
export const getDebugStep = () => DEBUG_CONFIG.step;

/**
 * Debug-aware console.log - only logs when debug is enabled
 */
export const debugLog = (...args) => {
	if (DEBUG_CONFIG.enabled) console.log(...args);
};

/**
 * Debug-aware console.error - only logs when debug is enabled
 */
export const debugError = (...args) => {
	if (DEBUG_CONFIG.enabled) console.error(...args);
};

/**
 * Get initial state overrides for debug mode
 * @param {object} WIZARD_STEPS - The wizard steps enum
 * @param {array} allSteps - Array of all step keys
 * @returns {object} State overrides to spread into initial state
 */
export const getDebugInitialState = (WIZARD_STEPS, allSteps) => {
	if (!DEBUG_CONFIG.enabled) return {};

	const targetStep =
		WIZARD_STEPS[DEBUG_CONFIG.step] || WIZARD_STEPS.TEMPLATE_SELECTION;
	const stepIndex = allSteps.indexOf(targetStep);

	return {
		currentStep: targetStep,
		completedSteps: stepIndex > 0 ? allSteps.slice(0, stepIndex) : [],
		templateSource: DEBUG_CONFIG.source,
		siteName: DEBUG_CONFIG.siteName,
		visibleSteps: allSteps, // Show all steps initially, will recalculate after data loads
	};
};

/**
 * Check if we should load template data (when jumping past template selection)
 * @param {object} WIZARD_STEPS - The wizard steps enum
 */
export const shouldLoadTemplateData = (WIZARD_STEPS) => {
	if (!DEBUG_CONFIG.enabled) return false;
	const targetStep = WIZARD_STEPS[DEBUG_CONFIG.step];
	return targetStep !== WIZARD_STEPS.TEMPLATE_SELECTION;
};

/**
 * Handle auto-selection of premade template after templates load
 * @param {array} templates - Loaded templates array
 * @param {function} setState - React setState function
 * @param {function} loadTemplateData - Function to load template data
 * @param {object} WIZARD_STEPS - The wizard steps enum
 */
export const handleDebugTemplateAutoSelect = (
	templates,
	setState,
	loadTemplateData,
	WIZARD_STEPS,
) => {
	if (!DEBUG_CONFIG.enabled) return;
	if (DEBUG_CONFIG.source !== "premade") return;
	if (!DEBUG_CONFIG.template) return;

	const template = templates.find((t) => t.key === DEBUG_CONFIG.template);

	if (template) {
		debugLog("[Debug] Auto-selecting template:", template.key);
		setState(
			{
				templateSource: "premade",
				selectedPremadeTemplate: template,
				selectedExistingSite: null,
			},
			() => {
				if (shouldLoadTemplateData(WIZARD_STEPS)) {
					loadTemplateData();
				}
			},
		);
	} else {
		debugError("[Debug] Template not found:", DEBUG_CONFIG.template);
		debugError(
			"[Debug] Available templates:",
			templates.map((t) => t.key),
		);
	}
};

/**
 * Handle auto-selection of existing site after sites load
 * @param {array} sites - Loaded sites array
 * @param {function} setState - React setState function
 * @param {function} loadTemplateData - Function to load template data
 * @param {object} WIZARD_STEPS - The wizard steps enum
 */
export const handleDebugSiteAutoSelect = (
	sites,
	setState,
	loadTemplateData,
	WIZARD_STEPS,
) => {
	if (!DEBUG_CONFIG.enabled) return;
	if (DEBUG_CONFIG.source !== "existing") return;
	if (!DEBUG_CONFIG.site) return;

	const site = sites.find((s) => s.Key === DEBUG_CONFIG.site);

	if (site) {
		debugLog("[Debug] Auto-selecting site:", site.Key);
		setState(
			{
				templateSource: "existing",
				selectedExistingSite: site,
				selectedPremadeTemplate: null,
			},
			() => {
				if (shouldLoadTemplateData(WIZARD_STEPS)) {
					loadTemplateData();
				}
			},
		);
	} else {
		debugError("[Debug] Site not found:", DEBUG_CONFIG.site);
		debugError(
			"[Debug] Available sites:",
			sites.map((s) => s.Key),
		);
	}
};

/**
 * Check if visible steps should be recalculated after data loads
 * @param {object} WIZARD_STEPS - The wizard steps enum
 */
export const shouldRecalculateVisibleSteps = (WIZARD_STEPS) => {
	if (!DEBUG_CONFIG.enabled) return false;
	const targetStep = WIZARD_STEPS[DEBUG_CONFIG.step];
	return (
		targetStep !== WIZARD_STEPS.TEMPLATE_SELECTION &&
		targetStep !== WIZARD_STEPS.SITE_NAME
	);
};
